import React from 'react';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import { useSubscription } from '@apollo/client';
import { useLoggedUser } from 'state/user';
import useCanvas from 'components/canvas/hooks/use-canvas';
import collaborativeUserCursorMovedOnSurface from 'graphql/surface/mutations/collaborating-user-cursor-moved-on-surface';
import collaborativeUserEnteredSurface from 'graphql/surface/mutations/collaborating-user-entered-surface';
import collaborativeUserLeftSurface from 'graphql/surface/mutations/collaborating-user-left-surface';
import CollaboratingCursor, {
  CollaboratingCursorProps,
} from 'routes/surface/components/collaborating-users-cursors/components/collaborating-cursor';
import { collaboratingUserCursorMovedOnSurfaceSubscription } from 'graphql/surface/subscriptions/collaborating-user-cursor-moved-on-surface';
import { collaboratingUserEnteredSurfaceSubscription } from 'graphql/surface/subscriptions/collaborating-user-entered-surface';
import { collaboratingUserLeftSurfaceSubscription } from 'graphql/surface/subscriptions/collaborating-user-left-surface';
import apolloClient from 'services/apollo-client';
import { userLeftTheAppSubscription } from 'graphql/surface-node/subscriptions/user-left-the-app-subscription';
import { getSessionToken } from 'services/storage';
import { generateRandomColor } from 'utils';
import { isUndefined, throttle } from 'lodash';
import { set, omit } from 'lodash/fp';
import { AnyObject } from 'types';

export interface CollaboratingUsersListenerProps {
  cursors?: CollaboratingCursorProps[];
}

const defaultProps = {};

/***
 *  On ws disconnect check if user has locked surfaces and unlock them...
 *
 *  publish an event when surface is shared and keep that data always in cache and based on that we can then decide to publish mouse movements
 *
 */

export function useCollaboratingUserCursors(): CollaboratingCursorProps[] {
  const { id: surface, isShared } = useCurrentSurface() || {};

  const [loggedUser] = useLoggedUser();

  const variables = { surface, user: loggedUser?.id };

  const [cursorsMap, setCursorMap] = React.useState({});

  const { stageInstance, getRelativePointerPosition } = useCanvas();

  const { updateCursorPosition } = useCollaboratingCursorOperations();

  React.useEffect(() => {
    stageInstance?.on('mousemove', (event) => {
      // We should update cursor position only if node is shared
      if (isShared) {
        const cursorPosition = getRelativePointerPosition();

        if (cursorPosition) {
          updateCursorPosition({ surface, cursorPosition });
        }
      }
    });

    return () => {
      stageInstance?.off('mousemove');
    };
  });

  useSubscription(collaboratingUserCursorMovedOnSurfaceSubscription, {
    variables,
    skip: isUndefined(loggedUser?.id),
    client: apolloClient,
    onSubscriptionData(options) {
      const data = subscriptionDataResolver(options.subscriptionData.data);

      if (data) {
        setCursorMap((cursorMap) => {
          if (!data.color) {
            data.color = generateRandomColor();
          }

          return set(data.user.id, data, cursorMap);
        });
      }
    },
  });

  useSubscription(collaboratingUserEnteredSurfaceSubscription, {
    variables,
    skip: isUndefined(loggedUser?.id),
    client: apolloClient,
    onSubscriptionData(options) {
      const data = subscriptionDataResolver(options.subscriptionData.data);
    },
  });

  useSubscription(collaboratingUserLeftSurfaceSubscription, {
    variables,
    skip: isUndefined(loggedUser?.id),
    client: apolloClient,
    onSubscriptionData(options) {
      const data = subscriptionDataResolver(options.subscriptionData.data);

      if (data) {
        setCursorMap((cursorMap) => omit(data.user.id, cursorMap));
      }
    },
  });

  useSubscription(userLeftTheAppSubscription, {
    client: apolloClient,
    onSubscriptionData: async (options) => {
      const data = subscriptionDataResolver(options.subscriptionData.data);

      if (data?.id) {
        setCursorMap((cursorMap) => omit(data.id as any, cursorMap));
      }
    },
  });

  return Object.values(cursorsMap);
}

function subscriptionDataResolver(data: AnyObject): AnyObject | null {
  if (data) {
    return Object.entries(data).reduce<AnyObject>(
      (data, [_, value]) => value,
      {},
    );
  }

  return null;
}

function CollaboratingUsersCursors(props: CollaboratingUsersListenerProps) {
  const collaboratingCursors = useCollaboratingUserCursors();

  const cursorComponents = collaboratingCursors.map((data) => (
    <CollaboratingCursor key={data.user.email} {...data} />
  ));

  return <>{cursorComponents}</>;
}

CollaboratingUsersCursors.defaultProps = defaultProps;

export function useCollaboratingCursorListeners(surface: string) {
  React.useEffect(() => {
    const input = {
      surface,
      cursorPosition: { x: 0, y: 0 },
    };
    // User entered

    collaborativeUserEnteredSurface({ input });

    return () => {
      //user left
      getSessionToken() && collaborativeUserLeftSurface({ input: { surface } });
    };
  }, [surface]);
}

export function useCollaboratingCursorOperations() {
  const idRef = React.useRef<number>(0);

  const updateCursorPosition = throttle((input: any) => {
    if (process.env.REACT_APP_DO_NOT_TRACK_CURSOR) {
      return;
    }

    const response = collaborativeUserCursorMovedOnSurface({
      input: { ...input, id: idRef.current },
    });

    idRef.current += 1;

    return response;
  }, 100);

  return { updateCursorPosition };
}

export default CollaboratingUsersCursors;
